import cx from "classnames";
import isNull from "lodash/isNull";
import upperFirst from "lodash/upperFirst";
import { IonIcon, IonLabel } from "@ionic/react";
import { memo, useMemo } from "react";

import s from "./RetryPaymentsOrderSummary.module.scss";
import promoTag from "../../../../assets/icons/promo-tag.svg";
import useWLPromoStore from "../../../../components/WLPromoField/store";
import { usePurchaseV2Store } from "../store";
import {
  getEntriesWithPromo,
  getPriceWithStripeCredits,
  getPointsWithPromo,
  getStripePriceValueWithPromo,
  getStripePriceWithPromo,
  getWLPromoEntries,
  getWLPromoPoints,
} from "../../../../services/promo";
import compact from "lodash/compact";
import { pricetagOutline } from "ionicons/icons";
import {
  formatDollarPriceDisplay,
  formatWordPluralize,
  useBreakpoint,
} from "../../../../../Common/utils/common";
import { isEmpty } from "lodash";

const RetryPaymentsOrderSummary = ({ mode }: { mode?: "dialog" }) => {
  const { selectedProduct } = usePurchaseV2Store();
  const { validWLPromo, validStripeCoupon } = useWLPromoStore();

  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  const rowTextColorClass = mode === "dialog" ? "dark" : "white";

  const selectedProductOriginalCost = useMemo(
    () => (!!selectedProduct && selectedProduct.price) || 0,
    [selectedProduct],
  );

  const selectedProductCost = useMemo(
    () =>
      !!selectedProduct
        ? validStripeCoupon !== undefined && !isNull(validStripeCoupon)
          ? getStripePriceValueWithPromo(
              selectedProduct.price,
              validStripeCoupon,
            )
          : validWLPromo !== undefined &&
              !isNull(validWLPromo) &&
              validWLPromo.stripeCreditDiscount !== undefined
            ? validWLPromo.isStripeCreditPercentOff
              ? selectedProduct.price -
                selectedProduct.price *
                  (validWLPromo.stripeCreditDiscount / 100)
              : selectedProduct.price - validWLPromo.stripeCreditDiscount
            : selectedProduct.price
        : 0,
    [selectedProduct, validStripeCoupon, validWLPromo],
  );
  const savings = useMemo(() => {
    let savings = 0;

    if (selectedProductCost !== selectedProductOriginalCost) {
      savings += selectedProductOriginalCost - selectedProductCost;
    }

    return savings > 0
      ? `saving ${formatDollarPriceDisplay(savings)}`
      : null;
  }, [selectedProductCost, selectedProductOriginalCost]);

  const extraEntries = useMemo(() => {
    const extra = !!selectedProduct
      ? validWLPromo !== undefined && !isNull(validWLPromo)
        ? getWLPromoEntries(validWLPromo, selectedProduct)
        : 0
      : 0;
    return extra;
  }, [validWLPromo, selectedProduct]);

  const extraPoints = useMemo(() => {
    const extra = !!selectedProduct
      ? validWLPromo !== undefined && !isNull(validWLPromo)
        ? getWLPromoPoints(validWLPromo, selectedProduct)
        : 0
      : 0;
    return extra || 0;
  }, [validWLPromo, selectedProduct]);

  const extraString = useMemo(() => {
    if (extraEntries > 0 && extraPoints > 0) {
      return `${extraEntries} ${formatWordPluralize("Entry", extraEntries)} & ${extraPoints} ${formatWordPluralize("Point", extraPoints)}`;
    } else if (extraEntries > 0 && extraPoints === 0) {
      return `${extraEntries} ${formatWordPluralize("Entry", extraEntries)}`;
    } else if (extraEntries === 0 && extraPoints > 0) {
      return `${extraPoints} ${formatWordPluralize("Point", extraPoints)}`;
    } else if (extraEntries === 0 && extraPoints === 0) {
      return null;
    }

    // Fallback for edge cases
    return "";
  }, [extraEntries, extraPoints]);

  return (
    <div
      className={cx(s.container, {
        [s.mobile]: isMobile,
        [s.dialog]: mode === "dialog",
      })}
    >
      {selectedProduct !== undefined && (
        <div className={s.membershipDetails}>
          <div className={s.row}>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
            >
              Entries:
            </IonLabel>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
            >
              {validWLPromo !== undefined &&
              !isNull(validWLPromo) &&
              validWLPromo.entryPoints !== undefined &&
              validWLPromo.entryPoints !== 0 ? (
                <>
                  <s
                    className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
                  >
                    {selectedProduct.entriesPerMonth}{" "}
                  </s>
                  &nbsp;
                  {getEntriesWithPromo(
                    selectedProduct.entriesPerMonth,
                    validWLPromo,
                  )}{" "}
                  {formatWordPluralize(
                    "Entry",
                    getEntriesWithPromo(
                      selectedProduct.entriesPerMonth,
                      validWLPromo,
                    ),
                  )}
                </>
              ) : (
                <>
                  {selectedProduct.entriesPerMonth}{" "}
                  {formatWordPluralize(
                    "Entry",
                    selectedProduct.entriesPerMonth,
                  )}
                </>
              )}
            </IonLabel>
          </div>
          <div className={s.row}>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
            >
              Locker Points:
            </IonLabel>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
            >
              {validWLPromo !== undefined &&
              !isNull(validWLPromo) &&
              validWLPromo.rewardPoints !== undefined &&
              validWLPromo.rewardPoints !== 0 ? (
                <>
                  <s
                    className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
                  >
                    {selectedProduct.rewardPointsPerMonth}{" "}
                  </s>
                  &nbsp;
                  {getPointsWithPromo(
                    selectedProduct.rewardPointsPerMonth,
                    validWLPromo,
                  )}{" "}
                  {formatWordPluralize(
                    "Point",
                    getPointsWithPromo(
                      selectedProduct.rewardPointsPerMonth,
                      validWLPromo,
                    ),
                  )}
                </>
              ) : (
                <>
                  {selectedProduct.rewardPointsPerMonth}{" "}
                  {formatWordPluralize(
                    "Point",
                    selectedProduct.rewardPointsPerMonth,
                  )}
                </>
              )}
            </IonLabel>
          </div>
          <div className={s.row}>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} regular gilroy ${rowTextColorClass}`}
            >
              Total:
            </IonLabel>
            <IonLabel
              className={`${isMobile ? "win-body" : "win-header-4"} semibold gilroy ${rowTextColorClass}`}
            >
              {validStripeCoupon !== undefined && !isNull(validStripeCoupon) ? (
                <>
                  <s
                    className={`${isMobile ? "win-body" : "win-header-4"} semibold gilroy ${rowTextColorClass}`}
                  >
                    {formatDollarPriceDisplay(selectedProduct.price)}
                  </s>
                  &nbsp;
                  {getStripePriceWithPromo(
                    selectedProduct.price,
                    validStripeCoupon,
                  )}
                </>
              ) : validWLPromo !== undefined &&
                !isNull(validWLPromo) &&
                validWLPromo.stripeCreditDiscount !== undefined &&
                validWLPromo.stripeCreditDiscount !== 0 ? (
                <>
                  <s
                    className={`${isMobile ? "win-body" : "win-header-4"} medium gilroy ${rowTextColorClass}`}
                  >
                    {formatDollarPriceDisplay(selectedProduct.price)}
                  </s>
                  &nbsp;
                  {getPriceWithStripeCredits(
                    selectedProduct.price,
                    validWLPromo.stripeCreditDiscount,
                    !!validWLPromo.isStripeCreditPercentOff,
                  )}
                </>
              ) : (
                <>{formatDollarPriceDisplay(selectedProduct.price)}</>
              )}
            </IonLabel>
          </div>
        </div>
      )}
      {(!isNull(savings) || !isNull(extraString)) && (
        <div className={s.promoBanner}>
          <IonIcon className={s.icon} icon={pricetagOutline} />
          <IonLabel className="wl-h4 gilroyMedium">
            {upperFirst(
              `${compact([savings, `${extraString ? `Get ${extraString}` : ""}`]).join(" & ")} with promotions`,
            )}
          </IonLabel>
        </div>
      )}
    </div>
  );
};

export default memo(RetryPaymentsOrderSummary);
